// @flow
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import {
  typography,
  color,
  FONT_WEIGHTS,
  SPACING,
  SIZES
} from '@tablekit/theme';
import { margin } from '@tablekit/utils';
import Button from '@tablekit/button';
import LazyLoad from 'react-lazyload';
import RichText from 'components/RichText';
import type {
  AssetType,
  BlokComponentPropsType,
  DocType,
  UrlType
} from 'components/types';
import Link, { isValidLink } from 'components/Link';
import Image from 'components/Image';
import columnWidth from 'utils/columnWidth';
import Card from 'styled/Card';
import { LAZY_LOAD_OFFSET, MAX_WIDTH, MOBILE_MAX_WIDTH } from 'utils/theme';

type CalloutType = {
  background_image?: AssetType,
  background_overlay?: number,
  body: DocType,
  // string for old image type, when CMS type is changed the underlying data doesn't till next save
  image?: string | AssetType,
  link?: UrlType,
  link_text?: string,
  sub_body?: DocType,
  title: string
};

const Wrapper = styled(Card, {
  shouldForwardProp: (prop) => ['useBackground'].indexOf(prop) === -1
})`
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  // IE11 fix
  background: ${color('primary')};
  ${(props) =>
    props.useBackground
      ? css`
          background-image: linear-gradient(
            136deg,
            ${color('primary')(props)} 2%,
            ${color('primary2')(props)} 100%
          );
        `
      : ''};
  margin: 0 -${SPACING.L4};

  @media (min-width: 361px) {
    width: ${columnWidth(10)}%;
    margin: 0 auto;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${SPACING.L4};

  @media (min-width: 361px) {
    padding: ${SPACING.L6};
  }

  @media (min-width: 1024px) {
    flex-direction: row;
  }

  @media (min-width: 1280px) {
    padding: ${SPACING.L7};
  }

  ${({ hasImage }) => (hasImage ? '' : `padding: ${SPACING.L7} !important;`)};
`;

const BackgroundImagePositioner = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;

  ${({ backgroundOverlay }) =>
    !backgroundOverlay
      ? ''
      : css`
          &:after {
            content: '';
            display: block;
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            z-index: 2;
            background-color: black;
            opacity: ${Math.max(30, Math.min(80, backgroundOverlay))}%;
          }
        `};
`;

const BackgroundImage = styled(Image)`
  height: 100%;
  min-width: 100%;
  object-fit: cover;
  z-index: 1;
`;

const Column = styled.div`
  display: flex;
  flex: 1 1 100%;
  flex-direction: column;
  justify-content: center;
  z-index: 2;

  &:last-child {
    text-align: center;
  }

  @media (min-width: 1024px) {
    width: 50%;
  }

  @media (min-width: 1280px) {
    ${margin({ left: SPACING.L7 })};
  }

  @media (min-width: 1440px) {
    ${margin({ left: SPACING.L8 })};
  }

  @media (min-width: 1920px) {
    ${margin({ left: SPACING.L9 })};
  }
`;

const TextColumn = styled(Column)`
  @media (min-width: 1280px) {
    margin: 0;
    min-width: 50%;
  }
`;

const Title = styled.h3`
  color: white;
  margin-bottom: ${SPACING.L2};
  font-weight: ${FONT_WEIGHTS.BOLD};
  ${typography('L6')};

  @media (min-width: 769px) {
    ${(props) => typography(props.hasImage ? 'L6' : 'L8')(props)}
  }
`;

const BodyBase = styled(RichText)`
  color: white;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  margin-bottom: ${SPACING.L6};
`;

const Body = styled(BodyBase)`
  ${(props) => {
    const type = typography('L4')(props);
    return css`
      p:not(:last-child) {
        margin-bottom: ${type.lineHeight};
      }
      ${type}
    `;
  }};
  @media (min-width: 769px) {
    ${(props) => {
      const type = typography(props.hasImage ? 'L4' : 'L6')(props);
      return css`
        p:not(:last-child) {
          margin-bottom: ${type.lineHeight};
        }
        ${type}
      `;
    }};
  }
`;

const SubBody = styled(BodyBase)`
  ${(props) => {
    const type = typography('L1')(props);
    return css`
      p:not(:last-child) {
        margin-bottom: ${type.lineHeight};
      }
      ${type}
    `;
  }};
`;

const ImageWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  > img {
    display: flex;
    flex: 1 1 100%;
    width: 100%;
  }

  > picture,
  > picture > img {
    display: flex;
    flex: 1 1 100%;
    max-width: 100%;
    max-height: 100%;
  }

  @media (min-width: 576px) {
    > img,
    > picture,
    > picture > img {
      width: auto;
      max-height: 250px;
      margin: 0 auto;
    }
  }

  @media (min-width: 768px) {
    > img,
    > picture,
    > picture > img {
      max-height: 300px;
    }
  }

  @media (min-width: 1024px) {
    margin-left: ${SPACING.L8};
    > img,
    > picture,
    > picture > img {
      height: auto;
      width: 100%;
    }
  }
`;
const imageHeight = 210;

const ButtonWrapper = styled.div`
  margin-bottom: ${SPACING.L4};
`;

export default function Callout({
  blok: {
    body,
    // $FlowFixMe mixed type
    image,
    link,
    title,
    sub_body: subBody,
    background_image: backgroundImage,
    link_text: linkText,
    background_overlay: backgroundOverlay
  }
}: BlokComponentPropsType<CalloutType>) {
  let linkElement = null;
  const hasImage = image && (image.length || image.filename);
  if (isValidLink(link)) {
    linkElement = (
      <ButtonWrapper>
        <Button
          component={Link}
          gaReference={`Callout; ${title}`}
          appearance="inverse"
          size={hasImage ? SIZES.REGULAR : SIZES.LARGE}
          link={link}
        >
          {linkText}
        </Button>
      </ButtonWrapper>
    );
  }
  return (
    <Wrapper useBackground={!backgroundImage}>
      {backgroundImage && (
        <BackgroundImagePositioner backgroundOverlay={backgroundOverlay}>
          <BackgroundImage
            src={backgroundImage.filename}
            alt={
              backgroundImage.alt ||
              backgroundImage.title ||
              backgroundImage.name
            }
            options={[
              {
                breakpoint: `(min-width: ${MAX_WIDTH})`,
                options: {
                  focal: backgroundImage.focus,
                  size: { width: 1280 }
                }
              },
              {
                breakpoint: '(min-width: 1280px)',
                options: {
                  focal: backgroundImage.focus,
                  size: { width: 1000 }
                }
              },
              {
                breakpoint: '(min-width: 866px)',
                options: {
                  focal: backgroundImage.focus,
                  size: { width: 668 }
                }
              },
              {
                options: {
                  isfocal: backgroundImage.focus,
                  size: { height: 600 }
                }
              }
            ]}
            cssFocus
          />
        </BackgroundImagePositioner>
      )}
      <Content hasImage={hasImage}>
        <TextColumn>
          <Title hasImage={hasImage}>{title}</Title>
          <Body hasImage={hasImage} richText={body} />
          {subBody && <SubBody richText={subBody} />}
          {linkElement}
        </TextColumn>
        {hasImage && (
          <Column>
            <ImageWrapper>
              <LazyLoad height={imageHeight} offset={LAZY_LOAD_OFFSET}>
                <Image
                  src={image.filename || image}
                  options={{
                    isFitIn: true,
                    fill: 'transparent',
                    size: { width: MOBILE_MAX_WIDTH, height: imageHeight }
                  }}
                  alt={image.alt || image.title || title}
                />
              </LazyLoad>
            </ImageWrapper>
          </Column>
        )}
      </Content>
    </Wrapper>
  );
}
